import React, { useRef } from 'react';
import styled from 'styled-components';
import { DataExtractorViewProps, pageIndexType } from './DataExtractorViewer';
import { Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { palette } from 'styles/color';
import { IconLeft, IconRight, IconPlus, IconMinus, IconDragDrop } from 'assets/icon';
import UploadButton from './UploadButton';
import { Button, Loading as LoadingComponent, Typography } from 'design-component';
import DndBox from './DndBox';
import { ButtonPropsType } from 'design-component/Button';
import { useTranslation } from 'react-i18next';
import ga, { EventClearCanvas } from 'util/gaConnect';
// import { JsonType } from 'pages/Playground/DocExtractor';
import { useAppSelector } from 'store/store';
import { userInfoSelector } from 'store/slices/userInfo';

interface ThumbnailViewerProps extends DataExtractorViewProps {
  thumbnails?: string[];
  data?: any;
  isLoading: boolean;
  width: number;
  ref?: React.RefObject<HTMLDivElement | null>;
  objRef?: React.RefObject<HTMLDivElement | null>;
  pageIndex: pageIndexType;
  onChangePageIndex?: Dispatch<SetStateAction<pageIndexType>>;
  onCancelExtract?: () => void;
  docExtension?: string;
  setSelectedType: DataExtractorViewProps['setSelectedType'];

  // sampleData?: any;
  // clearSampleData?: () => void;
  // schemaType: JsonType;
}

const OBJECT_TYPE = [
  { type: 'text' },
  { type: 'table' },
  { type: 'image' },
  { type: 'chart' },
  { type: 'shape' },
  { type: 'equation' },
  { type: 'header' },
  { type: 'footer' },
  { type: 'caption' }
] as const;

export type ObjType = (typeof OBJECT_TYPE)[number]['type'];

interface BoundaryBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
interface JsonElement {
  id: string;
  type: string;
  boundaryBox?: BoundaryBox;
  content: {
    src?: string;
    csv?: string;
    markdown?: string;
    json?: any;
  };
}

export default function ThumbnailViewer(props: ThumbnailViewerProps) {
  const { t } = useTranslation();
  const {
    file,
    setFile,
    docExtension,
    sampleData,
    clearSampleData,
    thumbnails,
    isLoading,
    width,
    ref,
    objRef,
    pageIndex,
    onChangePageIndex,
    onCancelExtract,
    data,
    selectedType,
    schemaType,
    setSelectedType
  } = props;
  const [ratio, setRatio] = useState<number>(0.75);
  const [inputValue, setInputValue] = useState(String(pageIndex.index + 1));
  const isSelectedTypeChange = useRef(false); // selectedType 변경 시 스크롤 방지 플래그
  const [selectedObj, setSelectedObj] = useState<ObjType | null>(null);
  const [extractionSummary, setExtractionSummary] = useState<Record<ObjType, number>>({
    text: 0,
    table: 0,
    caption: 0,
    footer: 0,
    header: 0,
    image: 0,
    shape: 0,
    chart: 0,
    equation: 0
  });
  const isCanvasEmpty = !data && !thumbnails?.length; // 캔버스 초기화 상태 여부

  const handleObjectClick = (_type: ObjType) => {
    setSelectedObj((prev) => (prev === _type ? null : _type));
  };

  const movePage = (move: 'prev' | 'next') => {
    const newIndex = move === 'prev' ? pageIndex.index - 1 : pageIndex.index + 1;
    if (!thumbnails || newIndex < 0 || newIndex >= thumbnails.length) return;
    const div = ref?.current;
    if (div) {
      // for scroll to left top
      div.style.left = '0px';
      div.style.top = '0px';
      div.style.width = '0px';
      div.style.height = '0px';
    }
    onChangePageIndex?.({ index: newIndex });
  };

  const changeRatio = (type: 'minus' | 'plus') => {
    const factor = 0.25;
    const newRatio = type === 'minus' ? ratio - factor : ratio + factor;
    if (newRatio > 3.1 || newRatio < 0.24) return;
    setRatio(newRatio);
  };

  const navigateToPageByInput = (
    value: string,
    thumbnails: string[] | undefined,
    onChangePageIndex: Dispatch<SetStateAction<pageIndexType>> | undefined,
    setInputValue: (value: string) => void,
    ref: React.RefObject<HTMLDivElement | null>
  ) => {
    if (!thumbnails) return;
    const trimmedValue = value.trim();
    let newPage = parseInt(trimmedValue, 10) - 1;

    // 0 입력 시 1페이지로 이동
    if (newPage === -1) {
      newPage = 0;
      setInputValue('1');
    }

    // 입력값이 유효하지 않을 경우 마지막 페이지로 이동
    if (!thumbnails || isNaN(newPage) || newPage < 0 || newPage >= thumbnails.length) {
      onChangePageIndex?.({ index: thumbnails.length - 1 });
      setInputValue(String(thumbnails.length));
    } else {
      onChangePageIndex?.({ index: newPage });

      const div = ref?.current;
      if (div) {
        div.style.left = '0px';
        div.style.top = '0px';
        div.style.width = '0px';
        div.style.height = '0px';
      }
    }
  };

  useEffect(() => {
    // selectedType 변경으로 인한 pageIndex 초기화 시 스크롤 방지함
    if (isSelectedTypeChange.current) {
      isSelectedTypeChange.current = false;
      return;
    }

    if (ref?.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [pageIndex]);

  useEffect(() => {
    setInputValue(String(pageIndex.index + 1));
  }, [pageIndex.index]);

  useEffect(() => {
    onChangePageIndex?.({ index: 0 });
    isSelectedTypeChange.current = true; // selectedType 변경 시 플래그 설정

    if (ref?.current) {
      ref.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedType]);

  // NOTE: 문서 변경 시 선택 초기화
  useEffect(() => {
    setSelectedObj(null);
  }, [data?.docName]);

  // 객체 네비게이션 렌더링
  const renderObjectNavigation = ({
    data,
    _idx,
    containerRef,
    fileType
  }: {
    data: any;
    _idx: pageIndexType;
    containerRef: React.RefObject<HTMLDivElement | null>;
    fileType: string;
  }) => {
    if (!data || !containerRef) return;

    const currentPageData = data.pages[_idx.index];
    setExtractionSummary(currentPageData.extractionSummary || {}); // 현재 페이지의 extractionSummary 정보 업데이트
    const elements = currentPageData?.elements || [];
    const navCanvas = containerRef?.current; // 객체 네비게이션을 그리는 div 캔버스 영역
    const pageWidth = currentPageData?.pageWidth; // 페이지의 실제 너비
    const pageHeight = currentPageData?.pageHeight; // 페이지의 실제 높이
    const canvasWidth = navCanvas?.offsetWidth || 1; // 캔버스 영역의 너비
    // const canvasHeight = navCanvas?.offsetHeight || 1; // 캔버스 영역의 높이

    if (!navCanvas) return;
    navCanvas.innerHTML = ''; // 기존 요소 제거

    const getPosition = (value: number) => (value * canvasWidth) / pageWidth;

    const createLabel = (element: JsonElement) => {
      const label = document.createElement('span');
      label.textContent = `${element.type}.${element.id}`;
      Object.assign(label.style, {
        position: 'absolute',
        left: '-1px',
        top: '-17px',
        padding: '2px 10px',
        height: '16px',
        boxSizing: 'border-box',
        backgroundColor: '#17A817',
        color: '#fff',
        fontSize: '12px',
        lineHeight: '16px',
        textAlign: 'center',
        opacity: selectedObj === element.type ? '1' : '0'
      });

      return label;
    };

    const createElementDiv = (element: JsonElement) => {
      const { top = 0, bottom = 0, left = 0, right = 0 } = element.boundaryBox || {};

      const elementDiv = document.createElement('div');

      if (selectedType === 'pdf' || fileType === 'pdf') {
        const originalWidth = navCanvas.offsetWidth || pageWidth;
        const originalHeight = navCanvas.offsetHeight || pageHeight;
        const widthRatio = originalWidth / pageWidth;
        const heightRatio = originalHeight / pageHeight;

        Object.assign(elementDiv.style, {
          position: 'absolute',
          left: `${left * widthRatio}px`,
          top: `${top * heightRatio}px`,
          width: `${(right - left) * widthRatio}px`,
          height: `${(bottom - top) * heightRatio}px`,
          border: '1px solid transparent',
          background: 'transparent',
          zIndex: '1000'
        });
      } else {
        Object.assign(elementDiv.style, {
          position: 'absolute',
          left: `${getPosition(left)}px`,
          top: `${getPosition(top)}px`,
          width: `${getPosition(right - left)}px`,
          height: `${getPosition(bottom - top)}px`,
          border: '1px solid transparent',
          background: 'transparent',
          zIndex: '1000'
        });
      }

      elementDiv.setAttribute('data-object-type', element.type);
      elementDiv.setAttribute('data-object-id', element.id);

      const label = createLabel(element);

      // chip 활성 동작
      if (selectedObj === element.type) {
        elementDiv.style.border = '2px solid #17A817';
        elementDiv.style.background = '#17A8171A';
        label.style.opacity = '1';
      }

      // hover 동작
      elementDiv.addEventListener('mouseenter', () => {
        if (selectedObj === element.type) return;

        // elementDiv.style.border = '1px solid #17A817';
        // elementDiv.style.background = '#17A8171A';
        // label.style.opacity = '1';
      });

      // elementDiv.addEventListener('mouseleave', () => {
      //   if (selectedObj === element.type) return;

      //   elementDiv.style.border = '1px solid transparent';
      //   elementDiv.style.background = 'transparent';
      //   label.style.opacity = '0';
      // });

      elementDiv.appendChild(label);

      return elementDiv;
    };

    elements.forEach((element: JsonElement) => {
      navCanvas.appendChild(createElementDiv(element));
    });
  };

  useEffect(() => {
    if (!objRef?.current) return;

    if (schemaType === 'full') {
      objRef.current.innerHTML = '';
      return;
    }

    if (schemaType === 'unified' && data) {
      renderObjectNavigation({
        data,
        _idx: pageIndex,
        containerRef: objRef,
        fileType: docExtension || ''
      });
    }
  }, [pageIndex, data, objRef, ratio, selectedObj, schemaType]);

  return (
    <ThumbnailViewerBase>
      <ViewerHeader>
        <div>{data?.docName || t('uploadFile')}</div>
        <ClearButton
          onClick={() => {
            if (sampleData || file) {
              clearSampleData?.();
            }

            // setFile(undefined);

            setSelectedType?.('upload');
            ga.event(EventClearCanvas);
          }}
          disabled={!(sampleData || data) || schemaType === 'full'}>
          {t('clearCanvas')}
        </ClearButton>
      </ViewerHeader>
      <ViewerHeader>
        <ObjectNavigation>
          {OBJECT_TYPE.map(({ type }) => {
            // 시트 포맷(xlsx) 에 한해 header, footer 비활성화
            const isHeaderFooterDisabled =
              docExtension === 'xlsx' && (['header', 'footer'] as ObjType[]).includes(type);

            const isDisabled =
              isCanvasEmpty ||
              schemaType === 'full' ||
              (extractionSummary[type] || 0) === 0 ||
              isHeaderFooterDisabled;

            return (
              <Chip
                key={type}
                $disabled={isDisabled}
                $isSelected={selectedObj === type}
                onClick={() => !isDisabled && handleObjectClick(type)}>
                {t(type)}
              </Chip>
            );
          })}
          <Chip $disabled={true}>{t('readingOrder')}</Chip>
        </ObjectNavigation>
      </ViewerHeader>

      <Body>
        {isLoading ? (
          <Loading onCancel={() => onCancelExtract?.()} />
        ) : thumbnails && thumbnails.length > 0 ? (
          <>
            <ViewerContent>
              <Scale key={`scale-${ratio}`} $ratio={ratio} $width={width}>
                <img src={thumbnails[pageIndex.index]} alt="thumbnail" width={width}></img>
                <Highlight ref={ref}></Highlight>
                <ObjNavCanvas ref={objRef} className="ObjNavCanvas" />
              </Scale>
            </ViewerContent>
            <Toolbar>
              <ToolbarIcon onClick={() => changeRatio('minus')}>
                <IconMinus
                  style={{
                    color: ratio <= 0.25 ? '#A6A6A7' : palette.white
                  }}
                />
              </ToolbarIcon>
              <div className="value">{`${(ratio * 100).toFixed(0)}%`}</div>
              <ToolbarIcon onClick={() => changeRatio('plus')}>
                <IconPlus
                  style={{
                    color: ratio >= 3.0 ? '#A6A6A7' : palette.white
                  }}
                />
              </ToolbarIcon>
              <div className="divider"></div>
              <ToolbarIcon onClick={() => movePage('prev')}>
                <IconLeft
                  style={{
                    color: palette.white
                  }}
                />
              </ToolbarIcon>
              <div className="pagenum">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    if (!thumbnails) return;
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val === '') {
                      setInputValue('');
                      return;
                    }
                    let num = parseInt(val, 10);
                    if (num > thumbnails.length) {
                      num = thumbnails.length;
                    }
                    setInputValue(String(num));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      navigateToPageByInput(
                        inputValue,
                        thumbnails,
                        onChangePageIndex,
                        setInputValue,
                        ref || { current: null }
                      );
                    }
                  }}
                  onBlur={() =>
                    navigateToPageByInput(
                      inputValue,
                      thumbnails,
                      onChangePageIndex,
                      setInputValue,
                      ref || { current: null }
                    )
                  }
                  style={{
                    width: `${(Math.max(inputValue.length, 1) + 1) * 11}px`,
                    minWidth: '33px',
                    padding: '6px 8px',
                    boxSizing: 'border-box',
                    backgroundColor: palette.gray[700],
                    border: 'none',
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    fontWeight: 400,
                    textAlign: 'center',
                    color: palette.white
                  }}
                />
                <div>of</div>
                <div>{thumbnails.length}</div>
              </div>
              <ToolbarIcon onClick={() => movePage('next')}>
                <IconRight
                  style={{
                    color: palette.white
                  }}
                />
              </ToolbarIcon>
            </Toolbar>
          </>
        ) : (
          <DragDrop
            onChange={(file) => setFile(file)}
            isFullSchemaSelected={schemaType === 'full'}
          />
        )}
      </Body>
    </ThumbnailViewerBase>
  );
}

const ThumbnailViewerBase = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${palette.gray[100]};
  display: flex;
  flex-direction: column;
`;

export const ViewerHeader = styled.div`
  width: 100%;
  height: 54px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  border-bottom: solid 1px ${palette.gray[200]};
`;

const ViewerContent = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding-top: 20px;
`;

const Scale = styled.div<{ $ratio: number; $width: number }>`
  width: fit-content;
  height: fit-content;
  position: relative;
  transform: scale(${({ $ratio }) => $ratio});
  transform-origin: ${({ $ratio }) => `${$ratio > 1 ? 'left top' : 'center top'}`};
`;

const Highlight = styled.div`
  position: absolute;
  background-color: #418dff1a;
  border: 1px solid var(--Primary, #277cfe);
`;

const ObjNavCanvas = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
`;

const Toolbar = styled.div`
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  bottom: 22px;
  transform: translate(-50%);
  border-radius: 4px;
  background-color: ${palette.gray[500]};
  box-shadow: 0px 0px 8px 0px #00000033;
  color: ${palette.white};
  gap: 8px;
  padding: 5px 20px;

  .divider {
    width: 1px;
    height: 22px;
    margin: 0 12px;
    background-color: ${palette.white};
    opacity: 0.15;
  }

  .pagenum {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: inherit;
  }

  .value {
    height: 29px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: black;
    border-radius: 8px;
    background-color: ${palette.gray[700]};
    padding: 6px 8px;
  }
`;

const ToolbarIcon = (props: PropsWithChildren<Pick<ButtonPropsType, 'onClick'>>) => (
  <Button variant="text" onClick={props.onClick} size="small">
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {props.children}
    </div>
  </Button>
);

export const ViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 19px;
`;

const Body = styled(ViewerContainer)`
  position: relative;
  flex: 1;
  overflow: auto;
`;

const DragDrop = (props: { onChange: (file: File) => void; isFullSchemaSelected?: boolean }) => {
  const { t } = useTranslation();
  const { isLogged } = useAppSelector(userInfoSelector);

  const handleFileChange = (file: File) => {
    if (!isLogged) return;
    props.onChange(file);
  };

  return (
    <DndBox onChange={handleFileChange}>
      <div
        style={{
          backgroundColor: `${palette.gray[200]}`,
          borderRadius: '50%',
          width: '88px',
          height: '88px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <IconDragDrop></IconDragDrop>
      </div>
      <CustomUploadButton
        onChange={props.onChange}
        isFullSchemaSelected={props.isFullSchemaSelected}
      />
      <Typography size="14px" weight={500} color={palette.gray[400]} align="center">
        {t('fileUploadLabel')}
        <br />
        {t('fileSizeLimitMessage')}
        <br />
        {`(${t('uploadLimit')})`}
      </Typography>
    </DndBox>
  );
};

const CustomUploadButton = styled(UploadButton)`
  border: 1px solid #e4e4e7;
`;

const Loading = (props: { onCancel: () => void }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <LoadingComponent />
      <Typography
        size="16px"
        weight={500}
        color={'#545454'}
        align="center"
        style={{
          margin: '19px'
        }}>
        {t('docsProcessing')}
      </Typography>
      <Button onClick={props.onCancel}>{t('cancel')}</Button>
      <Typography
        size="14px"
        weight={500}
        color={palette.gray[400]}
        align="center"
        style={{ marginTop: '19px' }}>
        {t('docProcessingCompleteMsg')}
        <br />
        {t('originalDocPreviewMsg')}
      </Typography>
    </div>
  );
};

const ClearButton = styled(Button)`
  &:disabled {
    color: ${palette.gray[500]};
  }
`;

// 스타일 정의
const ObjectNavigation = styled.div`
  display: flex;
  gap: 4px;
`;

const Chip = styled.div<{ $isSelected?: boolean; $disabled?: boolean }>`
  min-width: 70px;
  min-height: 32px;
  padding: 0 15px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 16px;
  border: 1px solid ${palette.gray[200]};
  background-color: ${palette.white};

  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s, color 0.2s;

  color: ${palette.gray[800]};
  text-align: center;
  font-weight: 500;
  font-size: 13px;
  line-height: 100%;

  ${({ $isSelected }) =>
    $isSelected &&
    `
    border: 1px solid ${palette.blue[500]};
    color : ${palette.blue[500]};
    background: ${palette.white};
  `}

  ${({ $disabled }) =>
    $disabled &&
    `
    background: ${palette.gray[100]};
    border: 1px solid ${palette.gray[300]};
    color: ${palette.gray[400]};
    cursor: not-allowed;
  `}
`;
